/**
 * Generic modal state management hook
 * Eliminates boilerplate for modal open/close/edit logic
 */

import { useState, useCallback } from 'react';

export interface UseModalOptions<T> {
  onOpen?: (item?: T) => void;
  onClose?: () => void;
}

export interface UseModalReturn<T> {
  isOpen: boolean;
  editingItem: T | null;
  openForCreate: () => void;
  openForEdit: (item: T) => void;
  close: () => void;
  toggle: () => void;
}

/**
 * Hook to manage modal state (open/close, create vs edit mode)
 *
 * @example
 * ```tsx
 * const { isOpen, editingItem, openForCreate, openForEdit, close } = useModal<User>({
 *   onClose: () => console.log('Modal closed')
 * });
 *
 * // Open for creating new item
 * <button onClick={openForCreate}>Add User</button>
 *
 * // Open for editing existing item
 * <button onClick={() => openForEdit(user)}>Edit</button>
 *
 * // In modal component
 * <Modal isOpen={isOpen} onClose={close}>
 *   {editingItem ? (
 *     <UserForm user={editingItem} mode="edit" />
 *   ) : (
 *     <UserForm mode="create" />
 *   )}
 * </Modal>
 * ```
 */
export function useModal<T = any>({ onOpen, onClose }: UseModalOptions<T> = {}): UseModalReturn<T> {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);

  /**
   * Open modal for creating a new item
   */
  const openForCreate = useCallback(() => {
    setEditingItem(null);
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  /**
   * Open modal for editing an existing item
   */
  const openForEdit = useCallback(
    (item: T) => {
      setEditingItem(item);
      setIsOpen(true);
      onOpen?.(item);
    },
    [onOpen]
  );

  /**
   * Close modal and clear editing state
   */
  const close = useCallback(() => {
    setIsOpen(false);
    setEditingItem(null);
    onClose?.();
  }, [onClose]);

  /**
   * Toggle modal state
   */
  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      openForCreate();
    }
  }, [isOpen, close, openForCreate]);

  return {
    isOpen,
    editingItem,
    openForCreate,
    openForEdit,
    close,
    toggle,
  };
}
